<script setup lang="ts">
import { computed } from "vue";
import { useGetMyLicenseSummary } from "@/stores/queries/organizations.query";
import { useDates } from "@/composable/useDates";
import { useTranslation } from "i18next-vue";

const { t } = useTranslation();
const { data, isLoading } = useGetMyLicenseSummary();
const { formatDistanceToNow } = useDates();

function progressStatus(used: number, quota: number) {
  if (quota === 0) return "success";
  const pct = (used / quota) * 100;
  if (pct >= 90) return "exception";
  if (pct >= 80) return "warning";
  return "success";
}

function progressPct(used: number, quota: number) {
  if (quota === 0) return 0;
  return Math.min(100, Math.round((used / quota) * 100));
}

function formatDate(date: string | null) {
  if (!date) return null;
  return new Date(date).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

const tierLabel = (tier: string | null) => {
  if (!tier) return "";
  return tier.charAt(0) + tier.slice(1).toLowerCase();
};

const subscription = computed(() => data.value?.subscription ?? null);

const drawingBoosts = computed(() =>
  (data.value?.drawingBoosts ?? [])
    .filter((b) => b.isActive)
    .sort((a, b) => {
      if (!a.endDate) return 1;
      if (!b.endDate) return -1;
      return new Date(a.endDate).getTime() - new Date(b.endDate).getTime();
    })
);

const reportBoosts = computed(() =>
  (data.value?.reportBoosts ?? [])
    .filter((b) => b.isActive)
    .sort((a, b) => {
      if (!a.endDate) return 1;
      if (!b.endDate) return -1;
      return new Date(a.endDate).getTime() - new Date(b.endDate).getTime();
    })
);

const allBoosts = computed(() => [
  ...drawingBoosts.value.map((b) => ({ ...b, boostType: "drawing" as const })),
  ...reportBoosts.value.map((b) => ({ ...b, boostType: "report" as const })),
]);

const hasContent = computed(
  () =>
    !!data.value?.subscription ||
    (data.value?.drawingBoosts.some((b) => b.isActive) ?? false) ||
    (data.value?.reportBoosts.some((b) => b.isActive) ?? false)
);

function licenseQuota(
  license: { quantity: number; licenseType: { includedDrawings: number; includedReports: number } },
  type: "drawings" | "reports"
) {
  const included =
    type === "drawings"
      ? license.licenseType.includedDrawings
      : license.licenseType.includedReports;
  return included * license.quantity;
}
</script>

<template>
  <div class="usage-block">
    <!-- Loading -->
    <div v-if="isLoading">
      <el-skeleton :rows="3" animated />
    </div>

    <!-- No subscription -->
    <div v-else-if="!hasContent" class="usage-empty">
      <i class="bi-credit-card-2-front usage-empty__icon"></i>
      <span>{{ t("usage.noSubscription") }}</span>
    </div>

    <template v-else>
      <!-- Subscription card -->
      <div v-if="subscription" class="sub-card">
        <div class="sub-card__top">
          <div class="sub-card__tier">
            {{ tierLabel(subscription.licenseType.tier) }}
          </div>
          <div v-if="subscription.endDate" class="sub-card__expiry">
            <i class="bi-calendar3"></i>
            {{ formatDistanceToNow(subscription.endDate) }}
          </div>
        </div>

        <div class="sub-card__bars">
          <div class="bar-row">
            <div class="bar-row__head">
              <span class="bar-row__label">
                <i class="bi-pencil"></i> {{ t("usage.drawings") }}
              </span>
              <span class="bar-row__count">
                {{ subscription.drawingsConsumed }} /
                {{ licenseQuota(subscription, "drawings") }}
              </span>
            </div>
            <el-progress
              :stroke-width="8"
              :percentage="
                progressPct(
                  subscription.drawingsConsumed,
                  licenseQuota(subscription, 'drawings')
                )
              "
              :status="
                progressStatus(
                  subscription.drawingsConsumed,
                  licenseQuota(subscription, 'drawings')
                )
              "
              :show-text="false"
            />
          </div>
          <div class="bar-row">
            <div class="bar-row__head">
              <span class="bar-row__label">
                <i class="bi-file-earmark-text"></i> {{ t("usage.reports") }}
              </span>
              <span class="bar-row__count">
                {{ subscription.reportsConsumed }} /
                {{ licenseQuota(subscription, "reports") }}
              </span>
            </div>
            <el-progress
              :stroke-width="8"
              :percentage="
                progressPct(
                  subscription.reportsConsumed,
                  licenseQuota(subscription, 'reports')
                )
              "
              :status="
                progressStatus(
                  subscription.reportsConsumed,
                  licenseQuota(subscription, 'reports')
                )
              "
              :show-text="false"
            />
          </div>
        </div>
      </div>

      <!-- Boosts -->
      <div v-if="allBoosts.length > 0" class="boosts-section">
        <div class="boosts-section__title">
          <i class="bi-lightning-charge-fill boosts-section__icon"></i>
          {{ t("usage.boosts") }}
        </div>

        <div
          v-for="boost in allBoosts"
          :key="boost.id"
          class="boost-card"
        >
          <div class="boost-card__head">
            <div class="d-flex align-items-center gap-2">
              <span
                class="boost-card__badge"
                :class="boost.boostType === 'drawing' ? 'boost-card__badge--drawing' : 'boost-card__badge--report'"
              >
                <i
                  :class="
                    boost.boostType === 'drawing'
                      ? 'bi-pencil-fill'
                      : 'bi-file-earmark-text-fill'
                  "
                ></i>
              </span>
              <div>
                <div class="boost-card__name">
                  {{
                    boost.boostType === "drawing"
                      ? t("usage.drawingBoost")
                      : t("usage.reportBoost")
                  }}
                </div>
                <div v-if="boost.endDate" class="boost-card__expiry">
                  {{ t("usage.expires") }} {{ formatDate(boost.endDate) }}
                </div>
              </div>
            </div>
            <span class="boost-card__count">
              {{
                boost.boostType === "drawing"
                  ? boost.drawingsConsumed
                  : boost.reportsConsumed
              }}
              /
              {{
                licenseQuota(
                  boost,
                  boost.boostType === "drawing" ? "drawings" : "reports"
                )
              }}
            </span>
          </div>
          <el-progress
            :stroke-width="6"
            :percentage="
              progressPct(
                boost.boostType === 'drawing'
                  ? boost.drawingsConsumed
                  : boost.reportsConsumed,
                licenseQuota(
                  boost,
                  boost.boostType === 'drawing' ? 'drawings' : 'reports'
                )
              )
            "
            :status="
              progressStatus(
                boost.boostType === 'drawing'
                  ? boost.drawingsConsumed
                  : boost.reportsConsumed,
                licenseQuota(
                  boost,
                  boost.boostType === 'drawing' ? 'drawings' : 'reports'
                )
              )
            "
            :show-text="false"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.usage-block {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

// --- Empty ---
.usage-empty {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--bs-secondary-color);
  font-style: italic;
  padding: 8px 0;

  &__icon {
    font-size: 16px;
    opacity: 0.5;
  }
}

// --- Subscription card ---
.sub-card {
  background-color: var(--el-bg-color-overlay);
  border: 1px solid var(--bs-border-color);
  border-radius: 10px;
  padding: 14px 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);

  &__top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 14px;
  }

  &__tier {
    font-weight: 700;
    font-size: 13px;
    color: var(--bs-primary);
    background-color: rgba(var(--bs-primary-rgb), 0.1);
    padding: 3px 10px;
    border-radius: 12px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  &__expiry {
    font-size: 11px;
    color: var(--bs-secondary-color);
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &__bars {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
}

// --- Bar rows ---
.bar-row {
  &__head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 3px;
  }

  &__label {
    font-size: 12px;
    font-weight: 500;
    color: var(--bs-body-color);
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &__count {
    font-size: 11px;
    font-weight: 600;
    color: var(--bs-secondary-color);
  }
}

// --- Boosts ---
.boosts-section {
  display: flex;
  flex-direction: column;
  gap: 8px;

  &__title {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--bs-secondary-color);
    display: flex;
    align-items: center;
    gap: 5px;
  }

  &__icon {
    color: #fd7e14;
    font-size: 13px;
  }
}

.boost-card {
  background-color: var(--el-bg-color-overlay);
  border: 1px solid var(--bs-border-color);
  border-radius: 8px;
  padding: 10px 14px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);

  &__head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 6px;
  }

  &__badge {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 6px;
    font-size: 12px;

    &--drawing {
      background: rgba(253, 126, 20, 0.12);
      color: #fd7e14;
    }

    &--report {
      background: rgba(111, 66, 193, 0.12);
      color: #6f42c1;
    }
  }

  &__name {
    font-weight: 600;
    font-size: 12px;
    color: var(--bs-body-color);
    line-height: 1.2;
  }

  &__expiry {
    font-size: 10px;
    color: var(--bs-secondary-color);
  }

  &__count {
    font-size: 11px;
    font-weight: 600;
    color: var(--bs-secondary-color);
  }
}
</style>
